/**
 * The level runtime: owns terrain, placed objects, entities and the player,
 * and advances the simulation one fixed 60Hz step at a time.
 *
 * The important design decision here is the **merged solid grid**. At load time
 * the terrain layer and the solidity of every placed object are flattened into
 * a single lookup table, so collision, enemy AI and the player all query one
 * array with no knowledge of which layer a wall came from.
 */

import { COURSE_TILES_H, COURSE_TILES_W, DT, TILE } from '../engine/constants'
import { Slope, type SolidQuery } from '../engine/collision'
import type { Entity } from '../engine/entity'
import {
  ONEWAY_TERRAIN,
  Solidity,
  SOLID_TERRAIN,
  Terrain,
  type LevelData,
  type LevelRuntime,
  type ObjectBehaviour,
  type ObjectPlacement,
  type PlayerLike,
  type ThemeId,
} from './types'
import {
  ENEMY_KIND,
  ENEMY_KIND_BY_ENTITY,
  placedObjectDef,
  type ObjectDef,
} from './objects'
import { sfxDeath, sfxWin } from '../core/audio'
import { OBJECT_BEHAVIOURS } from './behaviours'

/** Runtime instance of a placed object. */
export interface PlacedObject {
  /** Stable slot in Level.objects; resolve the current authoring index before removal. */
  index: number
  def: ObjectDef
  /** Tile position. */
  tx: number
  ty: number
  /** Merged props: registry defaults, then level overrides. */
  props: Record<string, number | string | boolean>
  behaviour: ObjectBehaviour | undefined
  /** Free scratch state for the behaviour. */
  state: Record<string, unknown>
  /** Set false when consumed/collected. */
  alive: boolean
  /** Push/slide offset applied by moving platforms. */
  ox: number
  oy: number
  /** Last step's offset, so a rider can be carried by the difference. */
  px: number
  py: number
  /**
   * The live entity this placement brought to life, or null.
   *
   * Enemies are *placed* as objects but *run* as entities, and the renderer
   * draws only the entity — an enemy placement with no entity is simply not on
   * screen. The handle is recorded here rather than looked up from the entity
   * list on the way out, because "which entity is this placement's" is exactly
   * the question a placement-index lookup gets wrong once anything has been
   * removed, and retiring a stranger's entity is worse than leaving a ghost.
   */
  enemy: Entity | null
}

/** A travelling solid, in pixel space. */
export interface DynamicSolid {
  x: number
  y: number
  w: number
  h: number
  /** Index of the owning placed object. */
  index: number
  ox: number
  oy: number
}

export type LevelStatus = 'playing' | 'dead' | 'won'

/** Game-wide counters surfaced in the HUD. */
export interface LevelStats {
  coins: number
  lives: number
  score: number
  time: number
}

export interface LevelCallbacks {
  onDeath?(): void
  onWin?(): void
}

export class Level implements LevelRuntime, SolidQuery {
  readonly data: LevelData
  readonly width: number
  readonly height: number
  readonly theme: ThemeId

  /** Terrain layer, one byte per tile. */
  private terrain: Uint8Array
  /**
   * Occupancy: 0 = empty, otherwise index+1 of the occupying placed object.
   * Consulted for object solidity and for object-triggered head bumps.
   */
  private occ: Uint32Array
  private solidGrid: Uint8Array
  private oneWayGrid: Uint8Array
  private slopeGrid: Uint8Array
  /** Slippery tiles, queried by the player to pick its movement profile. */
  private iceGrid: Uint8Array

  objects: PlacedObject[] = []
  entities: Entity[] = []
  player!: PlayerLike
  status: LevelStatus = 'playing'
  stats: LevelStats = { coins: 0, lives: 3, score: 0, time: 0 }
  /** Elapsed ticks, used for animation timing. */
  tick = 0
  /** Global switch state for On/Off platforms. */
  onOffActive = false
  /** Where the player respawns after dying. */
  spawnX = 0
  spawnY = 0

  private callbacks: LevelCallbacks
  private entityUid = 1

  constructor(data: LevelData, callbacks: LevelCallbacks = {}) {
    this.data = data
    this.width = data.width
    this.height = data.height
    this.theme = data.theme
    this.callbacks = callbacks

    const n = this.width * this.height
    this.terrain = new Uint8Array(n)
    for (let i = 0; i < n; i++) this.terrain[i] = data.tiles[i] ?? Terrain.EMPTY
    this.occ = new Uint32Array(n)
    this.solidGrid = new Uint8Array(n)
    this.oneWayGrid = new Uint8Array(n)
    this.slopeGrid = new Uint8Array(n)
    this.iceGrid = new Uint8Array(n)

    this.buildTerrainGrids()
    this.spawnObjects(data.objects)
    this.mergeSolidity()

    this.spawnX = data.startX * TILE
    this.spawnY = data.startY * TILE
    this.stats.time = data.time
  }

  // ── construction ───────────────────────────────────────────────────────

  private idx(tx: number, ty: number): number {
    return ty * this.width + tx
  }

  private inBounds(tx: number, ty: number): boolean {
    return tx >= 0 && ty >= 0 && tx < this.width && ty < this.height
  }

  private buildTerrainGrids(): void {
    for (let ty = 0; ty < this.height; ty++) {
      for (let tx = 0; tx < this.width; tx++) {
        const t = this.terrain[this.idx(tx, ty)] ?? Terrain.EMPTY
        if (SOLID_TERRAIN.has(t)) this.solidGrid[this.idx(tx, ty)] = 1
        if (ONEWAY_TERRAIN.has(t)) this.oneWayGrid[this.idx(tx, ty)] = 1
        if (t === Terrain.SLOPE_L) this.slopeGrid[this.idx(tx, ty)] = Slope.LEFT
        if (t === Terrain.SLOPE_R)
          this.slopeGrid[this.idx(tx, ty)] = Slope.RIGHT
      }
    }
  }

  private spawnObjects(placements: ObjectPlacement[]): void {
    placements.forEach((p, index) => {
      const def = placedObjectDef(p.id, p.props)
      if (def === undefined) {
        console.warn(`level: unknown object id "${p.id}" at index ${index}`)
        return
      }
      const obj: PlacedObject = {
        index: this.objects.length,
        def,
        tx: p.tx,
        ty: p.ty,
        props: { ...(def.props ?? {}), ...(p.props ?? {}) },
        behaviour: OBJECT_BEHAVIOURS[p.id],
        state: {},
        alive: true,
        ox: 0,
        oy: 0,
        px: 0,
        py: 0,
        enemy: null,
      }
      this.objects.push(obj)
      this.wakeEnemy(obj)
    })
  }

  /**
   * Bring one enemy placement to life, and remember what it became.
   *
   * Enemies are *placed* as objects but *run* as entities: the object is the
   * designer-facing placement, the entity is the live thing carrying AI and
   * physics. Every route that adds a placement has to do this identically, and
   * `addObject` used to skip it — which is why an enemy placed in the editor
   * was invisible: the renderer draws live entities and deliberately does not
   * draw enemy placements, so a placement with no entity never reached the
   * screen at all. It only showed up later, once something rebuilt the level.
   *
   * The placement's props are forwarded, and that has to be deliberate: a wisp
   * authored with `{ dir: -1 }` would otherwise come to life facing the other
   * way, silently, because the *object* kept the prop and the *entity* never
   * saw it. An authoring property that is accepted, stored, visible in the
   * editor and then thrown away is worse than one that does not exist.
   */
  private wakeEnemy(o: PlacedObject): void {
    const kind = ENEMY_KIND[o.def.id]
    if (kind === undefined) return
    const hb = o.def.hitbox
    o.enemy = this.spawn(
      kind,
      o.tx * TILE + (hb?.ox ?? 0),
      o.ty * TILE + (hb?.oy ?? 0),
      o.props,
    )
  }

  /**
   * Re-stamp the collision grid for any object that moved this step.
   *
   * The merged solid grid is built once at load, which is right for static
   * geometry and wrong for anything that travels: a moving platform was
   * *drawn* where it had moved to while still *colliding* where it was
   * authored. That is invisible in a screenshot and fatal in play -- it left a
   * permanent invisible wall at the authored position and made Star Ride's
   * island unreachable.
   *
   * Only the moved objects' own tiles are touched, and a tile is cleared only
   * if this object is genuinely its occupant, so terrain and overlapping
   * objects are never disturbed.
   */
  private restampMovedObjects(): void {
    for (const o of this.objects) {
      if (!o.alive) continue
      if (o.ox === o.px && o.oy === o.py) continue
      const solidity = o.def.solidity
      if (solidity === Solidity.NONE) continue
      if (o.def.dynamic === true) continue

      // Clear the old footprint, but only where this object is the occupant.
      this.eachFootprintTile(o, o.px, o.py, (tx, ty) => {
        const i = this.idx(tx, ty)
        if (this.occ[i] !== o.index + 1) return
        this.occ[i] = 0
        this.rebuildTileFromTerrain(tx, ty)
      })

      // Stamp the new one.
      this.eachFootprintTile(o, o.ox, o.oy, (tx, ty) => {
        const i = this.idx(tx, ty)
        this.occ[i] = o.index + 1
        if (solidity === Solidity.FULL) this.solidGrid[i] = 1
        else if (solidity === Solidity.ONEWAY) this.oneWayGrid[i] = 1
      })
    }
  }

  /**
   * Pixel-space rectangles of every travelling solid object, for collision
   * against things that are not on the tile grid.
   */
  dynamicSolids(): DynamicSolid[] {
    const out: DynamicSolid[] = []
    for (const o of this.objects) {
      if (!o.alive) continue
      if (o.def.dynamic !== true) continue
      if (o.def.solidity !== Solidity.FULL) continue
      const hb = o.def.hitbox
      out.push({
        x: o.tx * TILE + o.ox + (hb?.ox ?? 0),
        y: o.ty * TILE + o.oy + (hb?.oy ?? 0),
        w: hb?.w ?? o.def.w * TILE,
        h: hb?.h ?? o.def.h * TILE,
        // Which object this is, so a rider can be identified.
        index: o.index,
        ox: o.ox,
        oy: o.oy,
      })
    }
    return out
  }

  /** Visit every tile an object covers, given a pixel offset. */
  private eachFootprintTile(
    o: PlacedObject,
    ox: number,
    oy: number,
    fn: (tx: number, ty: number) => void,
  ): void {
    const x0 = Math.floor((o.tx * TILE + ox) / TILE)
    const y0 = Math.floor((o.ty * TILE + oy) / TILE)
    const x1 = Math.floor((o.tx * TILE + ox + o.def.w * TILE - 1) / TILE)
    const y1 = Math.floor((o.ty * TILE + oy + o.def.h * TILE - 1) / TILE)
    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        if (this.inBounds(tx, ty)) fn(tx, ty)
      }
    }
  }

  /** Restore one tile's collision flags from the terrain layer alone. */
  private rebuildTileFromTerrain(tx: number, ty: number): void {
    const i = this.idx(tx, ty)
    const t = this.terrain[i] ?? Terrain.EMPTY
    this.solidGrid[i] = SOLID_TERRAIN.has(t) ? 1 : 0
    this.oneWayGrid[i] = ONEWAY_TERRAIN.has(t) ? 1 : 0
  }

  /**
   * Move the player with any solid object they are standing on.
   *
   * A moving platform that does not carry its rider slides out from under them,
   * which makes every lift in the game unusable. The platform writes its
   * position into `ox`/`oy`; we snapshot the previous value in `step()` and
   * apply the difference to the player when their feet are resting on it.
   *
   * The test is deliberately forgiving (within a few pixels of the surface)
   * because the player is resolved *after* the platform has already moved, so
   * by the time we look they may be a pixel or two inside it.
   */
  private carryPlayer(): void {
    const p = this.player
    if (p === undefined) return
    for (const d of this.dynamicSolids()) {
      // The rider is identified by horizontal overlap with the platform and
      // feet resting on its top surface. The window is generous because the
      // player was last resolved against the platform's *previous* position.
      if (p.x + p.w < d.x || p.x > d.x + d.w) continue
      const feet = p.y + p.h
      if (feet < d.y - 6 || feet > d.y + 6) continue
      const owner = this.objects[d.index]
      p.y = d.y - p.h
      p.vy = 0
      if (owner !== undefined) p.x += d.ox - owner.px
      return
    }
  }

  /**
   * Put every placed enemy back at its authored position.
   *
   * Called on respawn. Without this, dying permanently clears a course of its
   * enemies, which turns any death into a difficulty *reduction* — the
   * opposite of what a checkpoint should do.
   */
  resetEnemies(): void {
    // Drop live enemies, but keep transient things (debris, puffs, fireballs).
    this.entities = this.entities.filter(
      (e) => !ENEMY_KIND_BY_ENTITY.has(e.kind),
    )
    // Rebuilt from the object's own merged props, for the same reason
    // `spawnObjects` does it: a respawned enemy has to be configured exactly as
    // the placement says, or a death silently re-randomises it. It also
    // re-points `o.enemy` at the new entity, so removing the placement later
    // retires the enemy that is actually running.
    for (const o of this.objects) {
      if (!o.alive) continue
      this.wakeEnemy(o)
    }
  }

  /**
   * Flatten terrain + solid objects into the single collision grids.
   *
   * Objects flagged `dynamic` are deliberately excluded: a travelling platform
   * straddles tile boundaries as it moves, so baking it into a tile grid makes
   * it two rows tall half the time and shoves riders up a whole tile. They are
   * collided as free AABBs by `dynamicSolids()` instead.
   */
  private mergeSolidity(): void {
    for (const o of this.objects) {
      if (!o.alive) continue
      if (o.def.dynamic === true) continue
      const s = o.def.solidity
      if (s === Solidity.NONE) continue
      for (let dy = 0; dy < o.def.h; dy++) {
        for (let dx = 0; dx < o.def.w; dx++) {
          const tx = o.tx + dx
          const ty = o.ty + dy
          if (!this.inBounds(tx, ty)) continue
          const i = this.idx(tx, ty)
          this.occ[i] = o.index + 1
          if (s === Solidity.FULL) this.solidGrid[i] = 1
          else if (s === Solidity.ONEWAY) this.oneWayGrid[i] = 1
          // Slippery is orthogonal to solidity, so it is recorded in its own
          // grid rather than as a terrain id: the player resolves it from the
          // tiles under its feet, and `terrain` cannot describe a placed
          // object at all.
          if (o.def.slippery === true) this.iceGrid[i] = 1
        }
      }
    }
  }

  // ── collision queries (SolidQuery) ─────────────────────────────────────

  isSolid(tx: number, ty: number): boolean {
    if (tx < 0 || tx >= this.width) return true // invisible walls at level edges
    if (ty < 0) return false
    if (ty >= this.height) return false // below the level is a pit, handled by death
    return this.solidGrid[this.idx(tx, ty)] === 1
  }

  isOneWay(tx: number, ty: number): boolean {
    if (!this.inBounds(tx, ty)) return false
    return this.oneWayGrid[this.idx(tx, ty)] === 1
  }

  /**
   * Slippery surface test, part of the `SolidQuery` contract.
   *
   * Read from the object layer, not the terrain: an `ice-block` occupies its
   * tile with solidity rather than with a terrain id, so `terrainAt` under an
   * ice run is `EMPTY`. That is exactly why the crate block's payload also
   * could not be read off the terrain.
   */
  isIce(tx: number, ty: number): boolean {
    if (!this.inBounds(tx, ty)) return false
    return this.iceGrid[this.idx(tx, ty)] === 1
  }

  slopeAt(tx: number, ty: number): Slope {
    if (!this.inBounds(tx, ty)) return Slope.NONE
    return this.slopeGrid[this.idx(tx, ty)] as Slope
  }

  /** Terrain id at a tile, for the renderer. */
  terrainAt(tx: number, ty: number): number {
    if (!this.inBounds(tx, ty)) return Terrain.EMPTY
    return this.terrain[this.idx(tx, ty)] ?? Terrain.EMPTY
  }

  /**
   * The placed *solid* object occupying a tile, if any.
   *
   * Reads the merged occupancy grid, which is only stamped for objects that
   * have solidity. A placed coin, expansion-cell or any other non-solid object is
   * therefore invisible here even though it is at this tile. The editor needs
   * those too and uses its own `objectAtTile` in `editor/tools.ts`; this
   * accessor is the tile-collision path, where non-solid objects are not
   * collided against anyway.
   */
  objectAt(tx: number, ty: number): PlacedObject | null {
    if (!this.inBounds(tx, ty)) return null
    const v = this.occ[this.idx(tx, ty)]
    if (v === 0) return null
    return this.objects[(v ?? 0) - 1] ?? null
  }

  // ── mutation ───────────────────────────────────────────────────────────

  /**
   * Write one terrain tile and its collision flags.
   *
   * Renderers read `terrainAt` every frame and rebuild what they draw from it,
   * so a write needs no invalidation step: the next frame shows the new tile.
   */
  setTerrain(tx: number, ty: number, t: number): void {
    if (!this.inBounds(tx, ty)) return
    const i = this.idx(tx, ty)
    this.terrain[i] = t
    this.solidGrid[i] = SOLID_TERRAIN.has(t) ? 1 : 0
    this.oneWayGrid[i] = ONEWAY_TERRAIN.has(t) ? 1 : 0
    this.slopeGrid[i] =
      t === Terrain.SLOPE_L
        ? Slope.LEFT
        : t === Terrain.SLOPE_R
          ? Slope.RIGHT
          : 0
  }

  /**
   * Rebuild the merged collision grids from terrain plus every placement.
   *
   * Every route that changes which objects exist — or where one collides —
   * needs this, and it used to be written out in full at each of them: one copy
   * per route, all of them having to stay identical.
   */
  private refreshSolidity(): void {
    this.solidGrid.fill(0)
    this.oneWayGrid.fill(0)
    this.slopeGrid.fill(0)
    this.occ.fill(0)
    this.iceGrid.fill(0)
    this.buildTerrainGrids()
    this.mergeSolidity()
  }

  addObject(placement: ObjectPlacement): void {
    const def = placedObjectDef(placement.id, placement.props)
    if (def === undefined) {
      console.warn(`level: cannot add unknown object id "${placement.id}"`)
      return
    }
    this.data.objects.push(placement)
    // Runtime slots stay stable while the authoring array shrinks on removal.
    const index = this.objects.length
    const obj: PlacedObject = {
      index,
      def,
      tx: placement.tx,
      ty: placement.ty,
      props: { ...(def.props ?? {}), ...(placement.props ?? {}) },
      behaviour: OBJECT_BEHAVIOURS[placement.id],
      state: {},
      alive: true,
      ox: 0,
      oy: 0,
      px: 0,
      py: 0,
      enemy: null,
    }
    this.objects.push(obj)
    // An enemy placement has to come to life here too, or the editor's object
    // brush and rectangle fill would lay down enemies that never appear.
    this.wakeEnemy(obj)
    // Re-merge so the new object is immediately solid.
    this.refreshSolidity()
  }

  removeObject(index: number): void {
    const placement = this.data.objects[index]
    if (placement === undefined) return
    const obj = this.objects.find(
      (o) =>
        o.alive &&
        o.def.id === placement.id &&
        o.tx === placement.tx &&
        o.ty === placement.ty,
    )
    this.data.objects.splice(index, 1)
    if (obj !== undefined) {
      obj.alive = false
      if (obj.enemy !== null) obj.enemy.alive = false
    }
    this.refreshSolidity()
  }

  /**
   * Resolve a runtime object's index inside `data.objects`.
   *
   * `PlacedObject.index` is its stable runtime slot, and the level keeps dead
   * objects in its runtime array (it only marks them `alive = false`),
   * so after a removal the two arrays drift: a stored index can name a different
   * placement. Trust it only while it still describes this exact object —
   * identity is the id and both corners, and one object per tile is what the
   * editor's `fits` enforces — and otherwise match on that identity.
   */
  placementIndexFor(o: PlacedObject): number {
    if (!o.alive) return -1
    const objects = this.data.objects
    const guess = objects[o.index]
    if (
      guess !== undefined &&
      guess.id === o.def.id &&
      guess.tx === o.tx &&
      guess.ty === o.ty
    ) {
      return o.index
    }
    return objects.findIndex(
      (p) => p.id === o.def.id && p.tx === o.tx && p.ty === o.ty,
    )
  }

  /**
   * Replace a placement's authoring props, in both layers.
   *
   * The editor's drop-an-item-on-a-block gesture writes props rather than adding
   * a placement, and a prop written only to `data` would be saved and drawn while
   * the live object kept playing by the old one. So the runtime object keeps its
   * identity — and with it its `state`, so a block that has already paid out
   * does not become fresh — while `def` is re-resolved, because a prop may carry
   * a footprint (`resize`) as well as a behaviour option.
   */
  setObjectProps(
    o: PlacedObject,
    patch: Record<string, number | string | boolean>,
  ): boolean {
    const index = this.placementIndexFor(o)
    if (index < 0) return false
    const placement = this.data.objects[index]
    if (placement === undefined) return false
    placement.props = { ...(placement.props ?? {}), ...patch }
    const def = placedObjectDef(placement.id, placement.props)
    if (def === undefined) return false
    if (o.alive) {
      o.def = def
      o.props = { ...(def.props ?? {}), ...placement.props }
    }
    this.refreshSolidity()
    return true
  }

  /**
   * Spawn a runtime entity, and hand it back so the caller can keep the handle.
   *
   * The return value is what lets a placed enemy be tied to its own entity; see
   * `PlacedObject.enemy`. `LevelRuntime` still declares this as `void`, because
   * everything else that spawns throws the result away.
   */
  spawn(
    kind: string,
    x: number,
    y: number,
    props: Record<string, unknown> = {},
  ): Entity | null {
    const factory = ENTITY_FACTORIES[kind]
    if (factory === undefined) {
      console.warn(`level: no entity factory for "${kind}"`)
      return null
    }
    const e = factory(x, y, props, this.entityUid++)
    this.entities.push(e)
    return e
  }

  addCoin(n = 1): void {
    this.stats.coins += n
  }

  addScore(n: number): void {
    this.stats.score += n
  }

  finish(): void {
    if (this.status !== 'playing') return
    this.status = 'won'
    // Flat clear bonus plus leftover clock. Time was already pressuring the
    // run via timeout; converting leftovers into score makes the
    // clock matter on a clean clear too.
    const secondsLeft = Math.max(0, Math.ceil(this.stats.time))
    this.timeBonus = secondsLeft * 50
    this.addScore(1000 + this.timeBonus)
    sfxWin()
    this.callbacks.onWin?.()
  }

  /** Seconds-left bonus awarded by the most recent `finish()`, else 0. */
  timeBonus = 0

  kill(): void {
    if (this.status !== 'playing') return
    this.status = 'dead'
    this.stats.lives -= 1
    sfxDeath()
    this.callbacks.onDeath?.()
  }

  /**
   * Hand the player this frame's travelling solids.
   *
   * Must be called immediately before `player.update`: the platform moves in
   * `step`, and the player has to be resolved against where it is *now*, not
   * where it was last frame.
   */
  publishDynamicSolids(): void {
    const p = this.player as { setDynamicSolids?: (r: DynamicSolid[]) => void }
    p.setDynamicSolids?.(this.dynamicSolids())
  }

  /** Reset the player to the spawn point without clearing the level. */
  respawn(): void {
    if (this.player.placeAt !== undefined)
      this.player.placeAt(this.spawnX, this.spawnY)
    else {
      this.player.x = this.spawnX
      this.player.y = this.spawnY
      this.player.vx = 0
      this.player.vy = 0
      this.player.alive = true
    }
    // An expired timed course needs a fresh clock; zero-time courses stay untimed.
    if (this.stats.time <= 0) this.stats.time = this.data.time
    // Re-arm placed enemies. A checkpoint should restore the challenge, not
    // hand the player a permanently cleared course.
    this.resetEnemies()
    if (this.status !== 'playing') {
      this.status = 'playing'
    }
  }

  setCheckpoint(tx: number, ty: number): void {
    this.spawnX = tx * TILE
    this.spawnY = ty * TILE
  }

  // ── simulation ─────────────────────────────────────────────────────────

  /** Advance the simulation by exactly one 1/60s step. */
  step(): void {
    if (this.status !== 'playing') return
    this.tick++

    if (this.stats.time > 0) {
      // The remaining clock becomes a bonus on clear, never passive score.
      this.stats.time -= DT
      if (this.stats.time <= 0) {
        this.stats.time = 0
        this.kill()
        return
      }
    }

    // Snapshot every placed object's offset *before* the behaviours run, so we
    // can work out how far each one moved this step.
    for (const o of this.objects) {
      if (!o.alive) continue
      o.px = o.ox
      o.py = o.oy
    }

    for (const o of this.objects) {
      if (!o.alive) continue
      o.behaviour?.update?.({
        tick: this.tick,
        level: this,
        player: this.player,
        state: o.state,
      })
    }

    this.restampMovedObjects()
    this.carryPlayer()

    for (const e of this.entities) {
      if (!e.alive) continue
      // Cheap cull: skip anything far off-screen in either direction.
      if (Math.abs(e.x - this.player.x) > TILE * 40) continue
      e.update?.(this, this.player)
    }

    // Reap dead entities, then reindex the remaining ones.
    if (this.entities.some((e) => !e.alive)) {
      this.entities = this.entities.filter((e) => e.alive)
    }
  }

  // ── serialisation ──────────────────────────────────────────────────────

  /** Snapshot back to a plain level object for saving. */
  toData(): LevelData {
    return {
      name: this.data.name,
      author: this.data.author,
      width: this.width,
      height: this.height,
      theme: this.theme,
      time: this.data.time,
      startX: this.data.startX,
      startY: this.data.startY,
      tiles: Array.from(this.terrain),
      objects: this.data.objects.map((o) => ({
        ...o,
        ...(o.props === undefined ? {} : { props: { ...o.props } }),
      })),
    }
  }
}

/** Entity factories, registered by `src/game/entities.ts`. */
export type EntityFactory = (
  x: number,
  y: number,
  props: Record<string, unknown>,
  uid: number,
) => Entity

/**
 * Populated by `src/game/entities.ts` at import time. Declared here to keep
 * this module free of import cycles.
 */
export const ENTITY_FACTORIES: Record<string, EntityFactory> = {}

/** Register an entity kind. Called from `entities.ts`. */
export function registerEntity(kind: string, factory: EntityFactory): void {
  ENTITY_FACTORIES[kind] = factory
}

/**
 * Object id -> entity kind, for the objects that place a live entity.
 *
 * A `shardling` in a level file is inert scenery until this maps it to the entity
 * that actually walks. Keeping the mapping here rather than in the entity
 * module means the object registry stays pure data.
 */
/** A blank course of the standard 10x2 screen size. */
export function emptyLevel(
  name = 'Untitled',
  theme: ThemeId = 'overworld',
): LevelData {
  return {
    name,
    width: COURSE_TILES_W,
    height: COURSE_TILES_H,
    theme,
    time: 300,
    startX: 2,
    startY: 10,
    tiles: new Array(COURSE_TILES_W * COURSE_TILES_H).fill(Terrain.EMPTY),
    objects: [],
  }
}
